import { Component, Host, Prop, h } from '@stencil/core';

import track from '../../../tracking/tracking.counteroffer';
import { formatCurrency } from '../../../utils/utils.currency';

import { Footer, ModalHeader } from './Shared';

@Component({
  tag: 'accept-or-counteroffer',
  styleUrl: 'accept-or-counteroffer.css',
})
export class AcceptOrCounteroffer {
  @Prop() offerAmount: string = '';
  @Prop() shouldDisplay: boolean = false;
  @Prop() amount: number = null;
  @Prop() isSubmitting: boolean = false;
  @Prop() inputAmount = null as number;
  @Prop() currentStep: number = 0;
  @Prop() error: string = '';
  @Prop() message: string = '';
  @Prop() confirmedDate: string = '';

  acceptOfferDialog: HTMLAcceptOfferDialogElement;
  counterofferDialog: HTMLCounterofferDialogElement;

  openAcceptModal = () => {
    this.acceptOfferDialog.modal.open();
    track.openModal.acceptOffer();
  };

  openCounterofferModal = () => {
    this.counterofferDialog.modal.open();
    track.openModal.counteroffer();
  };

  get accepted() {
    return this.amount === 0;
  }

  render() {
    if (!this.shouldDisplay) {
      return;
    }

    return (
      <Host>
        <div class="@container">
          <div class="bg-clrs-white rounded-lg shadow-drop mb-7 @xs:px-6 @md:px-12 py-6" data-testId="accept-or-counteroffer">
            {this.renderContent()}
          </div>
        </div>
        <accept-offer-dialog offerAmount={this.offerAmount} accepted={this.accepted} isSubmitting={this.isSubmitting} ref={(el) => (this.acceptOfferDialog = el)} />
        <counteroffer-dialog
          ref={(el) => (this.counterofferDialog = el)}
          isSubmitting={this.isSubmitting}
          offerAmount={this.offerAmount}
          currentStep={this.currentStep}
          error={this.error}
          message={this.message}
          inputAmount={this.inputAmount}
        />
      </Host>
    );
  }

  renderContent() {
    if (this.confirmedDate) {
      return this.renderAlreadySubmitted();
    } else if (this.accepted || this.amount) {
      return this.renderJustSubmitted();
    }
    return this.renderInteraction();
  }

  renderAlreadySubmitted() {
    const what = this.accepted ? 'an Offer acceptance' : 'a counteroffer';
    return (
      <div class="font-open-sans">
        <ModalHeader>
          <span>You have already submitted {what}.</span>
        </ModalHeader>

        <div class="text-sm my-6">
          On <strong>{this.confirmedDate}</strong> you submitted {what}.
          <br />A dealer will reach out to you about your Offer to set up an appointment. Please check your spam folder{' '}
          <span class="@md:inline-block">in case a dealer has already emailed you.</span>
        </div>
        <div class="text-xxs mb-4">
          <Footer />
        </div>
      </div>
    );
  }

  renderJustSubmitted() {
    return (
      <div class="text-center text-lg font-extrabold font-montserrat pb-11">
        <svg-icon icon="green-check-button" width="55" height="55" class="h-14 mb-5" />

        <div data-testid="accept-or-counteroffer-submitted-message">
          {this.amount ? (
            <span>Your Counteroffer of {formatCurrency(this.amount)} has been successfully submitted!</span>
          ) : (
            <span>Your Offer acceptance has been successfully submitted!</span>
          )}
        </div>
      </div>
    );
  }

  renderInteraction() {
    return (
      <div class="flex flex-col text-sm text-black font-open-sans">
        <h3 class="mb-1.5 text-lg font-montserrat font-extrabold">You Have Options! Accept This Offer or Make a Counteroffer</h3>
        <span class="pb-5">But don’t worry. You can accept the Instant Cash Offer now or make a counteroffer and you won’t be obligated to either one.</span>

        <div class="flex gap-6 flex-row-reverse my-5 @xs:flex-wrap @md:flex-nowrap">
          <div class="@xs:w-full @md:w-fit">
            <ico-button data-testId="accept-offer-btn" label="Accept Offer" buttonSize="large" onClick={this.openAcceptModal} />
          </div>
          <div class="@xs:w-full @md:w-fit">
            <ico-button data-testId="counteroffer-btn" label="Make a Counteroffer" buttonStyle="secondary" buttonSize="large" onClick={this.openCounterofferModal} />
          </div>
        </div>
        <div class="mb-4 text-xxs">
          <Footer />
        </div>
      </div>
    );
  }
}
